import { useState, useCallback, useRef, useEffect } from 'react';
import { ValidationSummary } from 'utils/ValidationUtils';
import { BreakLevel } from '@decisions/server/DecisionsFramework/BreakLevel';

let tooltipCounter = 0;

/**
 * Hook for all the things you would need to connect and show a
 * validation tooltip on hover.
 */
export function useHoverValidation(validation?: ValidationSummary, shouldShowProp = true) {
  if (IS_TEST) {
    // make these 100% consistent for snapshot tests.
    // webpack will remove this 'dead code' during real builds.
    tooltipCounter = 0;
  }
  const [toldToShow, setToldToShow] = useState(false);
  const shouldShow =
    shouldShowProp && (validation?.worstLevel === BreakLevel.Fatal || validation?.worstLevel === BreakLevel.Warning);
  const toolTipId = useRef(shouldShow ? `validation-tooltip-${++tooltipCounter}` : undefined);

  useEffect(() => {
    if (shouldShow && !toolTipId.current) {
      toolTipId.current = `validation-tooltip-${++tooltipCounter}`;
    } else if (!shouldShow && toolTipId.current) {
      toolTipId.current = undefined;
      tooltipCounter--;
    }

    return function cleanup() {
      // release counter when component gets cleaned up
      if (toolTipId.current) {
        tooltipCounter--;
      }
    };
  }, [shouldShow]);

  const [inputRef, setInputRef] = useState<HTMLElement>();
  // TODO get the appropriate class name in here, too?
  const onShowValidation = useCallback((e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
    setInputRef(e.currentTarget);
    setToldToShow(true);
  }, []);

  const onHideValidation = useCallback(() => {
    setInputRef(undefined);
    setToldToShow(false);
  }, [setInputRef]);

  return {
    inputRef,
    show: toldToShow && shouldShow,
    onShowValidation,
    onHideValidation,
    valInputAttributes: { 'aria-describedby': toolTipId.current },
    toolTipId: toolTipId.current,
  };
}
